import {useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {openVideoPlayer, setProgress} from "../../store/player/playerSlice";
import Keys from "../../constants/helper";
import {enqueueSnackbar} from "notistack";
import {ISerialData} from "../../store/player/playerSlice";
import {snackbarPosBottomRight} from "../../components/Notification/types";
import {useAddToListMutation} from "../../api/modules";
import {RootState} from "../../store";
import {AUTO_NEXT_RESET_PROGRESS, AUTO_NEXT_TRIGGER_PROGRESS} from "../../constants";
import {useTranslation} from "react-i18next";

export const usePlayerControls = (
        id: number | null,
        title: string | undefined,
        name: string | undefined,
        type: string | undefined,
        serialData: ISerialData | null | undefined,
        isVideoPlayerOpen: boolean,
        handleSeek: (newTime: number) => void,
    ) => {
        const {t} = useTranslation("translations");
        const dispatch = useDispatch();
        const profileID = localStorage.getItem(Keys.EPIC_PROFILE_ID);
        const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
        const [isVisible, setIsVisible] = useState(false);
        const [shouldAnimateOut, setShouldAnimateOut] = useState(false);
        const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState<number>(serialData?.episodeNumber || 0);
        const [controlsVisible, setControlsVisible] = useState(true);
        const [handleAddToList] = useAddToListMutation()
        const {progress, duration} = useSelector((state: RootState) => state.videoPlayer);
        const [hasTriggeredNext, setHasTriggeredNext] = useState(false);

        let hideControlsTimeout: NodeJS.Timeout;

        // Controls visibility management
        const handleMouseMove = () => {
            setControlsVisible(true);
            clearTimeout(hideControlsTimeout);
            hideControlsTimeout = setTimeout(() => {
                    setControlsVisible(false)
                    anchorEl && setAnchorEl(null)
                }
                , 4000);
        };

        useEffect(() => {
            if (isVideoPlayerOpen) {
                window.addEventListener("mousemove", handleMouseMove);
            }

            return () => {
                window.removeEventListener("mousemove", handleMouseMove);
                clearTimeout(hideControlsTimeout);
            };
        }, [isVideoPlayerOpen]);

        // Layout toggling for episodes list
        const handleLayout = () => {
            if (isVisible) {
                setShouldAnimateOut(true);
                setTimeout(() => {
                    setIsVisible(false);
                    anchorEl && setAnchorEl(null)
                    setShouldAnimateOut(false);
                }, 500);
            } else {
                setIsVisible(true);
            }
        };

        // Audio/subtitle popover handling
        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            // Toggle menu
            if (anchorEl === event.currentTarget) {
                setAnchorEl(null);
            } else {
                setAnchorEl(event.currentTarget);
            }
        };


        // Episode navigation
        const handleEpisodeChange = (
            seasonNumber: number,
            episodeNumber: number,
            withoutClose?: boolean
        ) => {
            // Find the selected season based on the season number
            const selectedSeason = serialData?.seasons?.find(
                (season) => season.season_number === seasonNumber
            );
            if (selectedSeason) {
                // Now find the selected episode from the season's chapters
                const selectedEpisode = selectedSeason.chapters?.find(
                    (chapter) => chapter.chapter_number === episodeNumber
                );
                if (selectedEpisode?.video) {
                    dispatch(openVideoPlayer({
                        id: id,
                        title: selectedEpisode.title as string,
                        name: name as string,
                        type: type,
                        poster_path: "",
                        videoData: selectedEpisode.video,
                        serialData: {
                            seasons: serialData?.seasons,
                            seasonNumber,
                            episodeNumber,
                            chapters: selectedSeason.chapters, // Use the chapters from the selected season
                            episodeTitle: selectedEpisode.title || ""
                        }
                    }));
                    dispatch(setProgress(0))
                    setCurrentEpisodeIndex(episodeNumber);
                    if (!withoutClose && isVisible) handleLayout();
                }
            }
        };


        const handleNextEpisode = () => {
            if (!serialData?.seasons?.length || !serialData.seasonNumber) return false;

            const currentSeasonIndex = serialData.seasons.findIndex(
                (season) => season.season_number === serialData.seasonNumber
            );
            if (currentSeasonIndex === -1) return false;

            const currentSeason = serialData.seasons[currentSeasonIndex];
            const currentSeasonChapters = serialData.chapters || currentSeason.chapters || [];
            const currentChapterNumber = currentEpisodeIndex || serialData.episodeNumber;
            const currentArrayIndex = currentSeasonChapters.findIndex(
                (chapter) => chapter.chapter_number === currentChapterNumber
            );

            if (currentArrayIndex === -1) return false;

            const hasNextEpisodeInSeason = currentArrayIndex < currentSeasonChapters.length - 1;
            if (hasNextEpisodeInSeason) {
                const nextEpisode = currentSeasonChapters[currentArrayIndex + 1];
                handleEpisodeChange(serialData.seasonNumber, nextEpisode.chapter_number);
                return true;
            }

            const nextSeason = serialData.seasons[currentSeasonIndex + 1];
            const firstEpisodeNextSeason = nextSeason?.chapters?.[0];
            if (nextSeason && firstEpisodeNextSeason) {
                handleEpisodeChange(nextSeason.season_number, firstEpisodeNextSeason.chapter_number);
                return true;
            }

            return false;
        };

        const handlePrevEpisode = () => {
            if (serialData?.chapters) {
                const currentArrayIndex = serialData.chapters.findIndex(
                    (chapter) => chapter.chapter_number === currentEpisodeIndex
                );
                const prevIndex = currentArrayIndex - 1;
                if (prevIndex >= 0 && prevIndex < serialData.chapters.length) {
                    const prevEpisode = serialData.chapters[prevIndex];
                    handleEpisodeChange(serialData.seasonNumber as number, prevEpisode.chapter_number);
                }
            }
        };

        // Progress bar interaction
        const handleSeekClick = (e: React.MouseEvent<HTMLDivElement>, duration: number) => {
            const {left, width} = e.currentTarget.getBoundingClientRect();
            const clickX = e.clientX - left;
            const newProgress = (clickX / width) * 100;
            const newTime = (newProgress / 100) * duration;
            handleSeek(newTime);
        };

        // Add to user list
        const handleAddList = async () => {
            try {
                if (profileID && id) {
                    await handleAddToList({params: {profileID}, body: {content: id}}).unwrap()
                    enqueueSnackbar(t("Messages.addedToMyListSuccess", {title}), {
                        anchorOrigin: snackbarPosBottomRight,
                        variant: "success"
                    });
                }
            } catch (error) {
                console.error("Failed to add to list", error);
            }
        };

        useEffect(() => {
            const isNearEnd = progress >= AUTO_NEXT_TRIGGER_PROGRESS;

            if (isNearEnd && !hasTriggeredNext && serialData?.seasons?.length) {
                const advanced = handleNextEpisode();
                if (advanced) {
                    setHasTriggeredNext(true);
                }
            }

            if (progress < AUTO_NEXT_RESET_PROGRESS && hasTriggeredNext) {
                setHasTriggeredNext(false);
            }


        }, [progress, hasTriggeredNext, currentEpisodeIndex, serialData]);

        const handleForward30 = () => {
            if (!duration) return;

            const currentTime = (progress / 100) * duration;
            const newTime = Math.min(currentTime + 30, duration);

            handleSeek(newTime);
        };

        const handleBackward30 = () => {
            if (!duration) return;

            const currentTime = (progress / 100) * duration;
            const newTime = Math.max(currentTime - 30, 0);

            handleSeek(newTime);
        };

        return {
            anchorEl,
            isVisible,
            shouldAnimateOut,
            controlsVisible,
            currentEpisodeIndex,
            handleLayout,
            handleClick,
            handleSeekClick,
            handleEpisodeChange,
            handleNextEpisode,
            handlePrevEpisode,
            handleAddList,
            setAnchorEl,
            handleForward30,
            handleBackward30
        };
    }
;
